import * as SliderPrimitive from '@radix-ui/react-slider';
import * as React from 'react';

import { cn } from '../../../utils';

interface Range {
  value: number;
  label: string;
}

interface SliderProps
  extends React.ComponentProps<typeof SliderPrimitive.Root> {
  showValueDisplay?: boolean;
  valueSeparator?: string;
  ranges?: Range[];
  label?: string;
  isFilter?: boolean;
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      defaultValue,
      value,
      min = 0,
      max = 100,
      step = 1,
      onValueChange,
      showValueDisplay = false,
      valueSeparator = '-',
      ranges,
      label,
      isFilter,
      ...props
    },
    ref,
  ) => {
    const initialValues = React.useMemo(() => {
      if (Array.isArray(value) && value.length >= 2) {
        return [value[0], value[1]];
      }
      if (Array.isArray(defaultValue) && defaultValue.length >= 2) {
        return [defaultValue[0], defaultValue[1]];
      }
      return [min, max];
    }, [value, defaultValue, min, max]);

    const [internalValues, setInternalValues] = React.useState(initialValues);

    const currentValues = value ? [value[0], value[1]] : internalValues;

    const isDiscreteMode = ranges && ranges.length > 0;
    const availableValues = isDiscreteMode
      ? ranges.map((range) => range.value).sort((a, b) => a - b)
      : [];

    const handleValueChange = React.useCallback(
      (values: number[]) => {
        let newMin = values[0];
        let newMax = values[1];

        if (isDiscreteMode && availableValues.length > 0) {
          const findClosest = (target: number) => {
            return availableValues.reduce((prev, curr) =>
              Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev,
            );
          };

          newMin = findClosest(values[0]);
          newMax = findClosest(values[1]);

          if (newMin > newMax) {
            [newMin, newMax] = [newMax, newMin];
          }
        }

        const newValues = [newMin, newMax];
        setInternalValues(newValues);
        onValueChange?.(newValues);
      },
      [isDiscreteMode, availableValues, onValueChange],
    );

    const getDisplayText = () => {
      if (isDiscreteMode && ranges) {
        const minLabel =
          ranges.find((range) => range.value === currentValues[0])?.label ||
          currentValues[0].toString();
        const maxLabel =
          ranges.find((range) => range.value === currentValues[1])?.label ||
          currentValues[1].toString();
        return `${minLabel} ${valueSeparator} ${maxLabel} ${label ?? ''}`;
      }
      return `${label ? `${label} ` : ''}${currentValues[0]} ${valueSeparator} ${label ? `${label} ` : ''}${currentValues[1]}`;
    };

    const getMarkers = () => {
      if (!isDiscreteMode || !ranges || ranges.length === 0) return [];
      return ranges.map((range, index) => ({
        position: ((range.value - min) / (max - min)) * 100,
        label: range.label,
        isFirst: index === 0,
        isLast: index === ranges.length - 1,
      }));
    };

    const markers = getMarkers();

    return (
      <div className="space-y-1 px-2">
        {showValueDisplay && (
          <div className={cn('text-center text-sm font-medium leading-none')}>
            {getDisplayText()}
          </div>
        )}

        <SliderPrimitive.Root
          ref={ref}
          data-slot="slider"
          value={currentValues}
          min={min}
          max={max}
          step={step}
          onValueChange={handleValueChange}
          className={cn(
            'relative flex w-full touch-none select-none items-center data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col data-[disabled]:opacity-50',
            className,
          )}
          {...props}
        >
          <SliderPrimitive.Track
            data-slot="slider-track"
            className={cn(
              'relative grow overflow-hidden rounded-full bg-gray-200 data-[orientation=horizontal]:h-2 data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-2',
            )}
          >
            <SliderPrimitive.Range
              data-slot="slider-range"
              className={cn(
                'absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
                {
                  'bg-evolve-highlight-500': isFilter,
                  'bg-evolve-primary': !isFilter,
                },
              )}
            />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            data-slot="slider-thumb"
            className={cn(
              'focus-visible:outline-hidden block size-5 shrink-0 rounded-full border-2 bg-white shadow-md transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 disabled:pointer-events-none disabled:opacity-50',
              {
                'hover:border-evolve-highlight-600 ring-evolve-highlight border-evolve-highlight':
                  isFilter,
                'hover:border-evolve-primary-600 ring-evolve-primary border-evolve-primary':
                  !isFilter,
              },
            )}
          />
          <SliderPrimitive.Thumb
            data-slot="slider-thumb"
            className={cn(
              'focus-visible:outline-hidden block size-5 shrink-0 rounded-full border-2 bg-white shadow-md transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 disabled:pointer-events-none disabled:opacity-50',
              {
                'hover:border-evolve-highlight-600 ring-evolve-highlight border-evolve-highlight':
                  isFilter,
                'hover:border-evolve-primary-600 ring-evolve-primary border-evolve-primary':
                  !isFilter,
              },
            )}
          />
        </SliderPrimitive.Root>

        {isDiscreteMode && markers.length > 0 && (
          <div className="relative">
            {markers.map((marker, index) => (
              <div
                key={index}
                className="text-evolve-secondary-900 absolute -translate-x-1/2 transform whitespace-nowrap text-[11px]"
                style={{ left: `${marker.position}%` }}
              >
                {marker.label}
              </div>
            ))}
          </div>
        )}

        {!isDiscreteMode && (
          <div className="flex justify-between px-2 text-xs text-slate-400">
            <span>{label ? `${label} ${min}` : min}</span>
            <span>{label ? `${label} ${max}` : max}</span>
          </div>
        )}
      </div>
    );
  },
);

Slider.displayName = 'Slider';

export { Slider };
